import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, GraduationCapIcon, UsersIcon, ClockIcon, DollarSignIcon, ShieldCheckIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const uagcEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const uagcStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: 'Associate',
  value: 'associate'
}, {
  label: "Bachelor's",
  value: 'bachelors'
}];
interface UagcProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const uagcPrograms: UagcProgram[] = [
// Associate - Business
{
  shortTitle: 'A.A. - Business',
  longTitle: 'Associate of Arts (A.A.) in Business',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '54516'
}, {
  shortTitle: 'A.A. - Organizational Management',
  longTitle: 'Associate of Arts (A.A.) in Organizational Management',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '54517'
},
// Associate - Education
{
  shortTitle: 'A.A. - Early Childhood Education',
  longTitle: 'Associate of Arts (A.A.) in Early Childhood Education',
  degreeLevel: 'associate',
  areaOfStudy: 'education',
  mProgram: '1639'
},
// Associate - Liberal Arts
{
  shortTitle: 'A.A. - Military Studies',
  longTitle: 'Associate of Arts (A.A.) in Military Studies',
  degreeLevel: 'associate',
  areaOfStudy: 'liberal-arts',
  mProgram: '54693'
},
// Bachelor's - Business
{
  shortTitle: 'B.A. - Accounting',
  longTitle: 'Bachelor of Arts (B.A.) in Accounting',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1610'
}, {
  shortTitle: 'B.A. - Business Administration',
  longTitle: 'Bachelor of Arts (B.A.) in Business Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1608'
}, {
  shortTitle: 'B.A. - Business Leadership',
  longTitle: 'Bachelor of Arts (B.A.) in Business Leadership',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1611'
}, {
  shortTitle: 'B.A. - Finance',
  longTitle: 'Bachelor of Arts (B.A.) in Finance',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1614'
}, {
  shortTitle: 'B.A. - Human Resources Management',
  longTitle: 'Bachelor of Arts (B.A.) in Human Resources Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1618'
}, {
  shortTitle: 'B.A. - Marketing',
  longTitle: 'Bachelor of Arts (B.A.) in Marketing',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1619'
}, {
  shortTitle: 'B.A. - Organizational Management',
  longTitle: 'Bachelor of Arts (B.A.) in Organizational Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1621'
}, {
  shortTitle: 'B.A. - Project Management',
  longTitle: 'Bachelor of Arts (B.A.) in Project Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1622'
},
// Bachelor's - Criminal Justice
{
  shortTitle: 'B.A. - Social and Criminal Justice',
  longTitle: 'Bachelor of Arts (B.A.) in Social and Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'criminal-justice',
  mProgram: '1642'
},
// Bachelor's - Education
{
  shortTitle: 'B.A. - Early Childhood Development w/ Differentiated Instruction',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Development w/ Differentiated Instruction',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1645'
}, {
  shortTitle: 'B.A. - Early Childhood Education',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1612'
}, {
  shortTitle: 'B.A. - Early Childhood Education Administration',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1613'
}, {
  shortTitle: 'B.A. - Education Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Education Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1637'
}, {
  shortTitle: 'B.A. - Instructional Design',
  longTitle: 'Bachelor of Arts (B.A.) in Instructional Design',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1641'
},
// Bachelor's - Information Technology
{
  shortTitle: 'B.A. - Business Information Systems',
  longTitle: 'Bachelor of Arts (B.A.) in Business Information Systems',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1609'
}, {
  shortTitle: 'B.A. - Information Technology',
  longTitle: 'Bachelor of Arts (B.A.) in Information Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1625'
}, {
  shortTitle: 'B.S. - Computer Software Technology',
  longTitle: 'Bachelor of Science (B.S.) in Computer Software Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1628'
}, {
  shortTitle: 'B.S. - Cyber & Data Security Technology',
  longTitle: 'Bachelor of Science (B.S.) in Cyber & Data Security Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1640'
},
// Bachelor's - Social & Behavioral Science
{
  shortTitle: 'B.A. - Applied Behavioral Science',
  longTitle: 'Bachelor of Arts (B.A.) in Applied Behavioral Science',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1643'
}, {
  shortTitle: 'B.A. - Psychology',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1671'
}, {
  shortTitle: 'B.A. - Sociology',
  longTitle: 'Bachelor of Arts (B.A.) in Sociology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1672'
}];
export function UAGCLandingPage() {
  usePageMeta('University of Arizona Global Campus | Online Degree Programs', 'Explore flexible online degree programs at The University of Arizona Global Campus. Accredited programs in Business, Criminal Justice, and Education designed for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    business: 'Business',
    'criminal-justice': 'Criminal Justice',
    education: 'Education',
    'information-technology': 'Information Technology',
    'liberal-arts': 'Liberal Arts',
    'social-behavioral-science': 'Social & Behavioral Science'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  // Get available areas based on selected degree level
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    uagcPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  // Get available programs based on selected area and degree level
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return uagcPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const currentProgram = uagcPrograms.find(p => p.mProgram === selectedProgram);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/uv7w6tl/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'The University of Arizona Global Campus',
            logo: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
            primaryColor: '#1C2B39',
            accentColor: '#CC0033',
            backUrl: '/colleges/university-of-arizona-global-campus',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-2075">
          <div data-id="element-2076">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2077">
              Degree Level
            </label>
            <div className="relative" data-id="element-2078">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800" data-id="element-2079">
                <option value="" data-id="element-2080">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-2081">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2082">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2083" />
              </div>
            </div>
          </div>

          <div data-id="element-2084">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2085">
              Area of Study
            </label>
            <div className="relative" data-id="element-2086">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-2087">
                <option value="" data-id="element-2088">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-2089">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2090">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2091" />
              </div>
            </div>
          </div>

          <div data-id="element-2092">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2093">
              Program
            </label>
            <div className="relative" data-id="element-2094">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-2095">
                <option value="" data-id="element-2096">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-2097">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2098">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2099" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-2100">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-2101">
          <div data-id="element-2102">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2103">
              Selected Program
            </label>
            <div className="relative" data-id="element-2104">
              <select id="step1-program" value={selectedProgram} onChange={e => {
              setSelectedProgram(e.target.value);
            }} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-2105">
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-2106">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-2107">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2108" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-2109" />

          <div data-id="element-2110">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2111">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-2112">
              <option value="" data-id="element-2113">-- Please Select --</option>
              {uagcEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-2114">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-2115">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-2116">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2117">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-2118">
              <option value="" data-id="element-2119">-- Please Select --</option>
              {uagcStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-2120">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-2121">{errors.startDate}</p>}
          </div>

          <div data-id="element-2122">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2123">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-2124">
              <option value="" data-id="element-2125">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-2126">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-2127">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-2128">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2129">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2130" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2131">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-2132">
          <div data-id="element-2133">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2134">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-2135" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-2136">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-2137">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-2138" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-2139">
                <div data-id="element-2140">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2141">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="City" data-id="element-2142" />
                </div>
                <div data-id="element-2143">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2144">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="State" data-id="element-2145" />
                </div>
              </div>
              <div data-id="element-2146">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2147">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`} placeholder="123 Main St" data-id="element-2148" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-2149">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-2150">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2151">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2152" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2153">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-2154">
          <div className="grid grid-cols-2 gap-3" data-id="element-2155">
            <div data-id="element-2156">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2157">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-2158" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2159">{errors.firstName}</p>}
            </div>
            <div data-id="element-2160">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2161">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-2162" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2163">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2164">
            <div data-id="element-2165">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2166">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-2167" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2168">{errors.email}</p>}
            </div>
            <div data-id="element-2169">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2170">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-2171" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2172">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2 pt-2" data-id="element-2173">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-2174">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#CC0033] focus:ring-[#CC0033] cursor-pointer flex-shrink-0" data-id="element-2175" />
              <span className="text-[10px] text-gray-500 leading-tight" data-id="element-2176">
                By selecting Request Information, I consent to receive calls,
                emails, and texts from the University of Arizona Global Campus
                (UAGC) which may be sent using automated or prerecorded
                technology. Message and data rates may apply; frequency may
                vary. This consent is not a condition of applying, enrolling, or
                purchase. I may contact UAGC directly at 866-711-1700, I may opt
                out of SMS anytime by replying STOP, and can text HELP for
                assistance.{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/uwnAfhD-utqnhD___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6NWFlZDplNzRhOWMyMmMxZmRmOTJlOGI0ZmI1NTU3NjBmMWQxMDRlN2M5NzVmNmRhN2M2NDY5YWFlNmZhNWZjYzc1OGZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-2177">
                  Privacy Policy
                </a>
                ,{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/yjwrx-fsi-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6ODA3ZTowMTFlY2JiNTVhZjhmMjE3ODhjMGMwYThmZDczMmVhMTk2MGI3ZmUzZGNmYWQ2NGM1M2FlMDY3ZTBmNjAxM2RiOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-2178">
                  Terms and Conditions
                </a>{' '}
                of Usage, and{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/xrx-yjwrx-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6OWIyNDpmZmQyOTUwMDZmNzIwZTRkMjdkY2NmOWU1NjZhMWM3OGM3N2QyNGE2YmExYmViZmZkYWU1MjQ5NTJjNjA5MjZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-2179">
                  SMS Terms and Conditions
                </a>{' '}
                apply.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-2180">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-2181">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2182">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2183" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#CC0033] to-[#A50028] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2184">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2185" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-2186">
      {/* Header */}
      <header className="bg-white py-3 px-6 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-2187">
        <img src="https://cdn.magicpatterns.com/uploads/hZrXUkDbsWP7copst5KXLY/UAGC.png" alt="University of Arizona Global Campus Logo" className="h-14 sm:h-16 w-auto object-contain" data-id="element-2188" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2A3F52] to-[#1A4D80] relative overflow-hidden" data-id="element-2189">
        {/* Decorative image overlay */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-2190" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 flex flex-col lg:flex-row items-center gap-10 lg:gap-16 relative z-10" data-id="element-2191">
          {/* Left Content */}
          <div className="flex-1 text-white max-w-xl" data-id="element-2192">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-2193">
              University of Arizona Global Campus
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6 text-white tracking-tight" data-id="element-2194">
              {currentProgram ? currentProgram.longTitle : <>
                  Flexible Online
                  <br data-id="element-2195" />
                  Degree Programs
                </>}
            </h1>
            <p className="text-lg text-white/85 max-w-lg leading-relaxed" data-id="element-2196">
              The University of Arizona Global Campus offers accredited online
              degree programs designed for working adults. With flexible
              scheduling, dedicated support, and a connection to the University
              of Arizona, we help you achieve your educational and career goals.
            </p>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-8 sm:p-10 flex-shrink-0 relative z-10" data-id="element-2197">
            <form onSubmit={e => e.preventDefault()} id="uagc-lead-form" data-id="element-2198">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-2199" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-2200" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-2201">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-2202">
                <div className="bg-gradient-to-r from-[#CC0033] to-[#A50028] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-2203"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-2204">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2205">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* Reasons to Choose UAGC */}
      <section className="bg-white py-16 border-b border-gray-200" data-id="element-2206">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2207">
          <div className="mb-10" data-id="element-2208">
            <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-2209"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39]" data-id="element-2210">
              Reasons to Choose UAGC
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2211">
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2212">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2213">
                <ClipboardCheckIcon className="w-7 h-7 text-[#CC0033]" data-id="element-2214" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-2215">
                No SAT, GMAT, GRE, or ACT Required
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2216">
                Standardized test scores are not required for enrollment.
              </p>
            </div>
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2217">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2218">
                <ClockIcon className="w-7 h-7 text-[#CC0033]" data-id="element-2219" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-2220">
                5- to 6-week Classes*
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2221">
                Take one class at a time as a full-time student.
              </p>
            </div>
            <div data-id="element-2222">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2223">
                <DollarSignIcon className="w-7 h-7 text-[#CC0033]" data-id="element-2224" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-2225">
                $0 Application Fee
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2226">
                Apply to UAGC risk-free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose UAGC */}
      <section className="py-16 bg-gray-50" data-id="element-2227">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2228">
          <div className="flex flex-col lg:flex-row items-start gap-12" data-id="element-2229">
            <div className="flex-1" data-id="element-2230">
              <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-2231"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-8" data-id="element-2232">
                Why Choose <span className="text-[#CC0033]" data-id="element-2233">UAGC</span>
              </h2>
              <ul className="space-y-4" data-id="element-2234">
                <li className="flex items-start" data-id="element-2235">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2236" />
                  <span className="text-gray-700" data-id="element-2237">
                    <strong data-id="element-2238">Transfer approved college credits</strong> towards
                    your bachelor's program*
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2239">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2240" />
                  <span className="text-gray-700" data-id="element-2241">
                    <strong data-id="element-2242">
                      College credit for relevant work/life experience
                    </strong>{' '}
                    through Prior Learning Assessment & Experiential Learning
                    Credit Programs
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2243">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2244" />
                  <span className="text-gray-700" data-id="element-2245">
                    <strong data-id="element-2246">Attend for 3 weeks risk-free</strong> to see if UAGC
                    is right for you
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2247">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2248" />
                  <div className="text-gray-700" data-id="element-2249">
                    <strong data-id="element-2250">
                      One course at a time in an accelerated format
                    </strong>
                    <ul className="mt-2 ml-4 space-y-1 text-sm text-gray-600" data-id="element-2251">
                      <li data-id="element-2252">• 5 weeks for undergraduate degrees</li>
                      <li data-id="element-2253">• 6 weeks for graduate programs</li>
                      <li data-id="element-2254">• 9 weeks for doctoral dissertation courses</li>
                    </ul>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2255">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2256" />
                  <span className="text-gray-700" data-id="element-2257">
                    <strong data-id="element-2258">Access the online classroom 24/7</strong> so you can
                    study at night, on the weekends, or during lunch breaks
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2259">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2260" />
                  <span className="text-gray-700" data-id="element-2261">
                    <strong data-id="element-2262">Learn from expert faculty</strong> with real-world
                    experience in their respective fields
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2263">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2264" />
                  <span className="text-gray-700" data-id="element-2265">
                    <strong data-id="element-2266">Join the Honors Program</strong> for high-achieving
                    bachelor's students
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:w-[400px] flex-shrink-0" data-id="element-2267">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=500&fit=crop&q=80" alt="Student studying online" className="w-full h-[400px] object-cover rounded-2xl shadow-lg" data-id="element-2268" />
            </div>
          </div>
        </div>
      </section>

      {/* Student Testimonials */}
      <section className="bg-[#1C2B39] py-16" data-id="element-2269">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2270">
          <div className="flex flex-col lg:flex-row gap-0 rounded-2xl overflow-hidden" data-id="element-2271">
            {/* Left intro */}
            <div className="bg-[#1C2B39] p-8 lg:p-12 flex flex-col justify-center lg:w-1/4" data-id="element-2272">
              <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" data-id="element-2273">
                Student Experience
              </p>
              <h2 className="text-3xl font-bold text-white leading-tight" data-id="element-2274">
                Praise for UAGC
              </h2>
              <p className="text-white/70 text-sm mt-3" data-id="element-2275">
                In their own words, why students love UAGC
              </p>
            </div>
            {/* Testimonial cards */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-0" data-id="element-2276">
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-2277">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop&q=80" alt="Vicente Rocco Vargas" className="w-full h-full object-cover" data-id="element-2278" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-2279"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2280">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-2281">
                    "I've tried to send so many people to UAGC because of how
                    well they've treated me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-2282">
                    Vicente "Rocco" Vargas
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-2283">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=500&fit=crop&q=80" alt="Jessica Smith" className="w-full h-full object-cover" data-id="element-2284" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-2285"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2286">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-2287">
                    "Life has totally changed because there were so many things
                    that came out of this experience for me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-2288">
                    Jessica Smith
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-2289">
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=500&fit=crop&q=80" alt="Wendy Bosquez" className="w-full h-full object-cover" data-id="element-2290" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-2291"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2292">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-2293">
                    "I believe that my future, my destiny is now bigger than my
                    dreams ever since being a part of UAGC."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-2294">
                    Wendy Bosquez
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation Section */}
      <section className="py-16 bg-white" data-id="element-2295">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2296">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-2297"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-6" data-id="element-2298">
            Accreditation = Quality Education
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2299">
            UAGC maintains institutional accreditation and several programmatic
            accreditations, which means that it consistently meets quality
            standards that all students deserve.
          </p>
          <p className="text-gray-600 leading-relaxed" data-id="element-2300">
            So, whether you are <strong data-id="element-2301">transferring credits†</strong> you have
            earned from another institution, or you are just{' '}
            <strong data-id="element-2302">starting your college journey</strong>, you can rest assured
            knowing you are receiving the most for your investment at UAGC.
          </p>
        </div>
      </section>

      {/* Try Your First Course Free */}
      <section className="py-16 bg-gray-50 border-t border-gray-200" data-id="element-2303">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2304">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-2305">
            Try Your First Course Complementary for 3 Weeks: No Cost, No Risk,
            Total Confidence
          </h2>
          <p className="text-gray-600 leading-relaxed mb-10" data-id="element-2306">
            The UAGC Promise affords new degree-seeking students the opportunity
            to get a feel for the UAGC online platform before enrolling to make
            sure it's the right fit. With the UAGC Promise, attend for the first
            three weeks of your first course without any financial risk.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-2307">
            <div className="flex flex-col items-center" data-id="element-2308">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-2309">
                <ShieldCheckIcon className="w-8 h-8 text-[#CC0033]" data-id="element-2310" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-2311">
                Attend for 3 Weeks Risk Complementary
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-2312">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-2313">
                <DollarSignIcon className="w-8 h-8 text-[#CC0033]" data-id="element-2314" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-2315">
                Start School with No Financial Burden
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-2316">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-2317">
                <LaptopIcon className="w-8 h-8 text-[#CC0033]" data-id="element-2318" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-2319">
                Flexibility to Find Your Fit
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1C2B39] py-16" data-id="element-2320">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2321">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-2322"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-2323">
            Interested in Learning More About Our{' '}
            <span className="text-[#D4A843]" data-id="element-2324">Degrees?</span>
          </h2>
          <div className="mt-8" data-id="element-2325">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#1C2B39] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-2326">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-2327">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-2328">
          <p data-id="element-2329">Certain degree programs may not be available in all states.</p>
          <p data-id="element-2330">
            * Classes last 5 weeks for undergraduate programs, 6 weeks for
            graduate programs, and 9 weeks for doctoral capstone, planning and
            project classes.
          </p>
          <p data-id="element-2331">
            † The transferability of credits is subject to the University of
            Arizona Global Campus transfer credit policies and requires the
            submission of official transcripts. The official transcripts will be
            evaluated to determine the credits that will officially apply toward
            a UAGC degree program.
          </p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-2332">
            The University of Arizona Global Campus is accredited by the Higher
            Learning Commission (hlcommission.org), an institutional
            accreditation agency recognized by the U.S. Department of Education.
          </p>
          <p className="text-gray-400" data-id="element-2333">
            © {new Date().getFullYear()} University of Arizona Global Campus.
            All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}